import type { Artifact } from '@/data/museum-data';

export interface ArtifactTagOverride {
  artifactId: string;
  tagsAdd: string[];
  tagsRemove: string[];
  category: string | null;
  keywords: string[];
  subjects: string[];
  curatorNotes: string | null;
}

export interface PersonTagOverride {
  personSlug: string;
  displayName: string | null;
  tags: string[];
  category: string | null;
  keywords: string[];
  subjects: string[];
  shortBio: string | null;
  curatorNotes: string | null;
}

let artifactOverrideMap = new Map<string, ArtifactTagOverride>();
let personOverrideMap = new Map<string, PersonTagOverride>();
const subscribers = new Set<() => void>();

const notify = () => subscribers.forEach((fn) => fn());

export const setArtifactTagOverrides = (rows: ArtifactTagOverride[]) => {
  artifactOverrideMap = new Map(rows.map((r) => [r.artifactId, r]));
  notify();
};

export const setPersonTagOverrides = (rows: PersonTagOverride[]) => {
  personOverrideMap = new Map(rows.map((r) => [r.personSlug, r]));
  notify();
};

export const subscribeTagOverrides = (fn: () => void) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};

export const getArtifactTagOverride = (artifactId: string): ArtifactTagOverride | undefined =>
  artifactOverrideMap.get(artifactId);

export const getPersonTagOverride = (slug: string): PersonTagOverride | undefined =>
  personOverrideMap.get(slug);

export const listArtifactTagOverrides = (): ArtifactTagOverride[] =>
  Array.from(artifactOverrideMap.values());

export const listPersonTagOverrides = (): PersonTagOverride[] =>
  Array.from(personOverrideMap.values());

const dedupe = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of arr) {
    const key = item.trim();
    if (!key) continue;
    const norm = key.toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(key);
  }
  return out;
};

export const mergeTags = (base: string[] | undefined, add: string[], remove: string[]): string[] => {
  const removeSet = new Set(remove.map((t) => t.trim().toLowerCase()));
  const combined = [...(base ?? []), ...add].filter(
    (t) => !removeSet.has(t.trim().toLowerCase()),
  );
  return dedupe(combined);
};

export const getEffectiveTags = (artifact: Pick<Artifact, 'id' | 'tags'>): string[] => {
  const override = artifactOverrideMap.get(artifact.id);
  if (!override) return artifact.tags ?? [];
  return mergeTags(artifact.tags, override.tagsAdd, override.tagsRemove);
};

export const getEffectiveCategory = (
  artifact: Pick<Artifact, 'id' | 'collectionCategory'>,
): string => {
  const override = artifactOverrideMap.get(artifact.id);
  return override?.category ?? artifact.collectionCategory;
};

export const getEffectiveKeywords = (artifactId: string): string[] =>
  artifactOverrideMap.get(artifactId)?.keywords ?? [];

export const getEffectiveSubjects = (artifactId: string): string[] =>
  artifactOverrideMap.get(artifactId)?.subjects ?? [];

/** Aggregate every known tag across base data + overrides, for autocomplete. */
export const collectAllTags = (artifacts: ReadonlyArray<Pick<Artifact, 'id' | 'tags'>>): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (t: string) => {
    const norm = t.trim().toLowerCase();
    if (!norm || seen.has(norm)) return;
    seen.add(norm);
    out.push(t.trim());
  };
  artifacts.forEach((a) => (a.tags ?? []).forEach(push));
  artifactOverrideMap.forEach((o) => o.tagsAdd.forEach(push));
  personOverrideMap.forEach((p) => p.tags.forEach(push));
  return out.sort((a, b) => a.localeCompare(b));
};